<template>
<b-overlay :show="isSiteDown" variant="secondary" opacity="0.87" z-index="2899">
  <div id="app" class="h-100" :class="[skinClasses]">
    <component :is="layout">
      <router-view />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />
  </div>

  <template #overlay>
    <div class="text-center card p-3">
      <div class="p-3">
        <b-spinner  style="width: 3rem; height: 3rem;" class="mb-3" label="Loading..."></b-spinner>
        <h2 id="sitedown-label" class="mb-2">Maintenance is currently running!</h2>
        <h4 id="sitedown-label" class="mb-1">Looks like Connect is currently down, but don't worry, we should be back in a few seconds!</h4>
      </div>
    </div>
  </template>
</b-overlay>
</template>

<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue';

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig';
import { provideToast } from 'vue-toastification/composition';
import { watch } from '@vue/composition-api';
import useAppConfig from '@core/app-config/useAppConfig';

import { useWindowSize, useCssVar } from '@vueuse/core';

import store from '@/store';

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue');
const LayoutHorizontal = () =>
  import('@/layouts/horizontal/LayoutHorizontal.vue');
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue');

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

    ScrollToTop
  },
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full';
      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    }
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      'primary',
      'secondary',
      'success',
      'info',
      'warning',
      'danger',
      'light',
      'dark'
    ];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      );
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
  },
  setup() {
    const { skin, skinClasses } = useAppConfig();
    const { enableScrollToTop } = $themeConfig.layout;

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout');

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade'
    });

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val);
    });

    return {
      skinClasses,
      enableScrollToTop
    };
  },
  data() {
    return {
      isSiteDown: false,
      registration: null,
      canDoRefresh: false,
      updateExists: false,
    }
  },
  mounted() {
    this.$store.commit('setVersion', process.env.VUE_APP_VERSION);
  },
  created() {
    //document.addEventListener('swSiteDown', this.siteWentDown)
    document.addEventListener('swSiteUp', this.siteWentUp)
    document.addEventListener('swActivated', this.swWasActivated, { once: true })
    document.addEventListener('swUpdated', this.updateAvailable, { once: true })
  },
  methods: {
    siteWentUp() {
      this.isSiteDown = false
    },
    // siteWentDown() {
    //   this.isSiteDown = true
    // },
    updateAvailable(event) {
      this.registration = event.detail
      this.updateExists = true
      if(this.registration && this.registration.waiting) {
        this.registration.waiting.postMessage({ type: 'skipWaiting' });
        this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
      this.messageAboutUpdate();
    },

    messageAboutUpdate() {
      this.canDoRefresh = true;
      this.$swal({
        title: 'New Version Detected!',
        text: "Ready to update to latest version of Connect?",
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes!',
        cancelButtonText: 'Later'
      }).then(result => {
        if (result.value) {
            window.location.reload();
            this.updateExists = false;
        }
      });
    }
  }
};
</script>

<style lang="scss">

</style>
